import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Import der eigenen Sammlung aus einem Poke-Genie-CSV.
 *
 * Der Import ersetzt die Sammlung vollständig — das steht bewusst gross
 * daneben, weil es sich nicht rückgängig machen lässt.
 */
export default function CollectionImport({ onImported }) {
  const [stand, setStand] = useState(null);
  const [bericht, setBericht] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const dateiFeld = useRef(null);

  useEffect(() => {
    api.collection.status().then(setStand).catch((e) => setFehler(e.message));
  }, []);

  const hochladen = async (datei) => {
    if (!datei) return;
    setLaeuft(true);
    setFehler(null);
    setBericht(null);
    try {
      const ergebnis = await api.collection.import(datei);
      setBericht(ergebnis);
      setStand(ergebnis);
      onImported?.();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaeuft(false);
      if (dateiFeld.current) dateiFeld.current.value = '';
    }
  };

  // Dieselbe Datei nochmal einlesen. Nach jeder Änderung an der Auswertung
  // will man den vorhandenen Stand erneut durchlaufen lassen — den Export
  // dafür jedes Mal neu heraussuchen zu müssen, ist unnötig.
  const wiederholen = async () => {
    setLaeuft(true);
    setFehler(null);
    setBericht(null);
    try {
      const ergebnis = await api.collection.repeat();
      setBericht(ergebnis);
      setStand(ergebnis);
      onImported?.();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaeuft(false);
    }
  };

  const leeren = async () => {
    if (!window.confirm('Die gesamte Sammlung löschen? Das lässt sich nicht rückgängig machen.')) return;
    try {
      setStand(await api.collection.clear());
      setBericht(null);
    } catch (e) {
      setFehler(e.message);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 32 }}>
      <h2 style={{ marginBottom: 8 }}>Eigene Sammlung importieren</h2>
      <p className="gruppe__text">
        CSV-Export aus Poke Genie. <strong>Ersetzt</strong> die bisherige Sammlung.
      </p>

      {stand?.lastFile && (
        <div className="letzte-datei">
          <div>
            <strong>{stand.lastFile.name}</strong>
            <div className="card__meta">
              {new Date(stand.lastFile.importedAt).toLocaleString('de-DE', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })} Uhr
              {stand.lastFile.entries !== null && ' · ' + stand.lastFile.entries + ' Einträge'}
            </div>
          </div>
          <button className="btn btn--ghost btn--klein" onClick={wiederholen} disabled={laeuft}>
            {laeuft ? 'Liest ein…' : 'Erneut einlesen'}
          </button>
        </div>
      )}

      {stand && (
        <div className="inline" style={{ marginBottom: 16 }}>
          <span className="badge badge--soft">{stand.total} Einträge</span>
          <span className="badge badge--soft">{stand.species} Arten</span>
          <span className="badge badge--soft">{stand.linked} im Katalog verknüpft</span>
          {stand.lastScanAt && (
            <span className="badge badge--soft">
              Letzter Scan: {new Date(stand.lastScanAt.replace(' ', 'T')).toLocaleDateString('de-DE')}
            </span>
          )}
        </div>
      )}

      <div className="inline">
        {/* Das native Datei-Feld sieht auf jeder Plattform anders aus und
            passt zu keiner. Der eigentliche Input wird deshalb versteckt und
            über das Label bedient — barrierefrei bleibt es trotzdem. */}
        <label className={`btn btn--primary${laeuft ? ' btn--aus' : ''}`}>
          {laeuft ? 'Wird eingelesen…' : 'CSV auswählen'}
          <input
            ref={dateiFeld}
            type="file"
            accept=".csv,text/csv"
            disabled={laeuft}
            className="datei-versteckt"
            onChange={(e) => hochladen(e.target.files?.[0])}
          />
        </label>
        {stand?.total > 0 && (
          <button className="btn btn--danger" onClick={leeren} disabled={laeuft}>
            Sammlung leeren
          </button>
        )}
      </div>

      {laeuft && <p className="card__meta" style={{ marginTop: 12 }}>Wird eingelesen…</p>}
      {fehler && <div className="error" style={{ marginTop: 16 }}>{fehler}</div>}

      {bericht && (
        <div className="import-bericht">
          <div className="inline" style={{ marginBottom: 10 }}>
            <span className="badge" style={{ '--badge-color': 'var(--accent-3)' }}>
              {bericht.imported} eingelesen
            </span>
            <span className="badge badge--soft">{bericht.matched} im Katalog gefunden</span>
            {bericht.skippedRows > 0 && (
              <span className="badge badge--soft">{bericht.skippedRows} Zeilen übersprungen</span>
            )}
            {bericht.flagsKept > 0 && (
              <span className="badge badge--soft">{bericht.flagsKept} Handpflege übernommen</span>
            )}
          </div>

          {bericht.flagsLost?.length > 0 && (
            <div className="error" style={{ marginBottom: 12 }}>
              Notizen verloren: {bericht.flagsLost.join(', ')}
            </div>
          )}

          {bericht.unmatched?.length > 0 && (
            <details className="import-offen">
              <summary>
                {bericht.unmatched.length} Arten sind noch nicht im Katalog
              </summary>
              <div className="inline">
                {bericht.unmatched.map((name) => (
                  <span className="badge badge--soft" key={name}>{name}</span>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
